import { AppBar, Box, Button, Grid2, Toolbar, Typography } from '@mui/material';
import AmountBreakDown from 'components/AmountBreakDown/AmountBreakDown';
import { Loader } from 'components/Loader/Loader';
import { config } from 'config/config';
import { useUserSearchContext } from 'context/UserSearchContext';
import { useState } from 'react';
import { ExtrasRentalRate, RentalRate } from 'types/BookingDetails';

export const PaymentToolBar: React.FC<{
  onClick?: () => void;
}> = ({ onClick }) => {
  const [open, setOpen] = useState(false);
  const { bookingSearchData, loading } = useUserSearchContext();
  const prePaidPrice: RentalRate = bookingSearchData.RentalRate.find(
    (rentalRate: RentalRate) => rentalRate.Code === 'PPD'
  );

  const popUpAction = () => {
    setOpen(!open);
  };

  const Label = ({ text, sx = {} }) => (
    <Typography
      variant="body1"
      sx={{
        borderRadius: '5px',
        width: 'max-content',
        padding: '0px 10px',
        marginBottom: '10px',
        ...sx,
      }}
    >
      {text}
    </Typography>
  );

  const PriceBlock = ({ label, value, numberOfDays, highlight, onClick }) => (
    <Box sx={{ width: highlight ? '35%' : '50%' }}>
      {label}
      <Typography>
        <span className="currency-tag">{config.CURRENCY} </span>
        <span className="price">{value?.toFixed(2)}</span>
        <span className="booking-duration">
          {numberOfDays > 1
            ? `for ${numberOfDays} days`
            : `for ${numberOfDays} day`}
        </span>
      </Typography>
      {onClick && (
        <Button
          variant="text"
          sx={{
            textTransform: 'none',
            padding: 0,
            minWidth: 'unset',
            color: '#235fb5',
            fontSize: '1rem',
            textDecoration: 'underline',
            '&:hover': { backgroundColor: 'transparent' },
          }}
          onClick={onClick}
        >
          View Details
        </Button>
      )}
    </Box>
  );

  const validRentals =
    bookingSearchData?.RentalRate?.filter(
      (rental: RentalRate) =>
        // bookingSearchData?.chargeCodes?.includes(rental.Code) &&
        ['TAR', 'DIT', 'PPD'].includes(rental.Code)
      // ['TAR', 'DIT'].includes(rental.Code)
    ) || [];

  const totalAmount = validRentals.reduce(
    (sum: number, rental: RentalRate) => sum + (rental?.TotalAmt || 0),
    0
  );

  const discount = bookingSearchData?.PromoCodeDis ?? 0;

  const finalAmount =
    discount > 0 ? totalAmount * (1 - discount / 100) : totalAmount;

  // Insurance Total
  const insuranceTotal =
    bookingSearchData?.RentalRate?.reduce((sum: number, rate: RentalRate) => {
      if (
        bookingSearchData.chargeCodes.includes(rate.Code) &&
        config.insuranceChargeCode.includes(rate.Code)
      ) {
        return sum + (rate.TotalAmt || 0);
      }
      return sum;
    }, 0) || 0;

  // Ancillaries Total
  const ancillariesTotal =
    bookingSearchData?.RentalRate?.reduce((sum: number, rate: RentalRate) => {
      if (
        bookingSearchData.chargeCodes.includes(rate.Code) &&
        config.ancilarriesChargeCode.includes(rate.Code)
      ) {
        return sum + (rate.TotalAmt || 0);
      }
      return sum;
    }, 0) || 0;

  // Extras Total
  const extrasTotal =
    bookingSearchData?.ExtrasRentalRate?.reduce(
      (sum: number, extra: ExtrasRentalRate) => {
        if (bookingSearchData.chargeCodes.includes(extra.Code)) {
          return sum + (extra.TotalAmt || 0);
        }
        return sum;
      },
      0
    ) || 0;

  const grandTotal =
    finalAmount + insuranceTotal + ancillariesTotal + extrasTotal;

  if (loading) return <Loader />;

  return (
    <>
      <AmountBreakDown open={open} onClose={popUpAction} />
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'unset',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          margin: 'auto',
          bottom: 0,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '30px 30px 0px 0px',
          }}
        >
          <Grid2
            container
            spacing={0}
            sx={{
              width: '90%',
              margin: 'auto',
              justifyContent: 'space-between',
            }}
          >
            <Grid2
              container
              // size={{ xs: 10, sm: 10, md: 10 }}
              sx={{ width: '65%' }}
            >
              <PriceBlock
                label={
                  <Label
                    text="Pay Later"
                    sx={{
                      background: '#EFF9FF',
                      border: '1px solid #D5DEE4',
                    }}
                  />
                }
                value={
                  Number(grandTotal.toFixed(2)) +
                  Math.abs(prePaidPrice?.TotalAmt)
                }
                numberOfDays={bookingSearchData.RentalDays}
                onClick={popUpAction}
                highlight={undefined}
              />
              <PriceBlock
                label={
                  <Label
                    text={`Pay Now & Save AED ${Math.abs(prePaidPrice ? Math.abs(prePaidPrice.TotalAmt) : 0).toFixed(2)}`}
                    sx={{
                      background: '#03466D',
                      color: '#fff',
                      textAlign: 'center',
                    }}
                  />
                }
                value={Number(grandTotal.toFixed(2))}
                numberOfDays={bookingSearchData.RentalDays}
                highlight
                onClick={undefined}
              />
            </Grid2>
            <Grid2 size={{ xs: 2 }}>
              <Button
                className="btn"
                sx={{
                  width: '150px',
                  height: 'auto',
                  minHeight: '50px',
                  maxHeight: '50px',
                }}
                onClick={onClick}
              >
                Continue
              </Button>
            </Grid2>
          </Grid2>
        </Toolbar>
      </AppBar>
    </>
  );
};
